import { complementOf } from "../../utils/percentage";
import {removeLoadingState, setVisibility} from "../helpers";

/**
 * Initializes IBAN edit/display toggle and second IBAN percentage logic.
 *
 * Expects the following data attributes on #ibanEditContainer:
 *   data-original-first-iban          – prefilled first IBAN value
 *   data-original-first-percentage    – prefilled first percentage value
 *   data-original-second-iban         – prefilled second IBAN value
 *   data-first-iban-id                – DOM id of the first IBAN input
 *   data-first-percentage-id          – DOM id of the first percentage input
 *   data-second-iban-id               – DOM id of the second IBAN input
 *   data-second-percentage-id         – DOM id of the second percentage input
 *   data-choice-second-iban-enabled   – "true" if 2nd IBAN feature is on
 *   data-iban-status                  – current IbanApplicationStatus value
 */
export function initIbanEditPartial() {
    const container = document.getElementById("ibanEditContainer");
    if (!container) {
        console.info('Iban edit container not found.');
        return;
    }
    const confirmIbanForm = document.getElementById("ConfirmIbanForm");

    if (!confirmIbanForm) {
        console.error('Iban edit form not found');
        return;
    }
    
    confirmIbanForm.addEventListener("sds:form:beforeSubmit", submitIbanForm);
   
    const displayContainer = document.getElementById("ibanDisplayContainer");
    const choice2ndEnabled = container.dataset.choiceSecondIbanEnabled === "true";
    const ibanStatus = (container.dataset.ibanStatus || "").toLowerCase();

    const firstIbanId = container.dataset.firstIbanId;
    const firstPercentageId = container.dataset.firstPercentageId;
    const secondIbanId = container.dataset.secondIbanId;
    const secondPercentageId = container.dataset.secondPercentageId;

    const originalFirstIban = container.dataset.originalFirstIban || "";
    const originalFirstPercentage = container.dataset.originalFirstPercentage || "";
    const originalSecondIban = container.dataset.originalSecondIban || "";

    // --- Edit / display toggle ---
    function setEditMode(isEdit) {
        if (container) container.hidden = !isEdit;
        if (displayContainer) displayContainer.hidden = isEdit;
    }

    // --- Cancel edit ---
    function cancelEdit(e) {
        e.preventDefault();

        const firstIbanInput = document.getElementById(firstIbanId);
        if (firstIbanInput) firstIbanInput.value = originalFirstIban;

        if (choice2ndEnabled) {
            const firstPercentageInput = document.getElementById(firstPercentageId);
            if (firstPercentageInput) firstPercentageInput.value = originalFirstPercentage;

            const secondIbanInput = document.getElementById(secondIbanId);
            if (secondIbanInput) secondIbanInput.value = originalSecondIban;

            const secondPercentageInput = document.getElementById(secondPercentageId);
            if (secondPercentageInput) secondPercentageInput.value = 0;

            setVisibilityIbanContainerItems( false);
        }

        setEditMode(false);
    }

    const cancelBtn = document.getElementById("cancelEditIbanBtn");
    if (cancelBtn) cancelBtn.onclick = cancelEdit;

    const toggleBtn = document.getElementById("toggleEditModeBtn");
    if (toggleBtn) {
        toggleBtn.onclick = function (e) {
            e.preventDefault();
            setEditMode(true);
        };
    }

    const isEditMode = ibanStatus === "new";
    setEditMode(isEditMode);

    // --- Second IBAN percentage logic ---
    if (!choice2ndEnabled) return;

    const firstPercentageInput = document.getElementById(firstPercentageId);
    const secondPercentageInput = document.getElementById(secondPercentageId);

    if (!firstPercentageInput || !secondPercentageInput) return;

    secondPercentageInput.disabled = true;

    setVisibilityIbanContainerItems( parseInt(firstPercentageInput.value) < 100);

    firstPercentageInput.addEventListener("keyup", handleChange);
    firstPercentageInput.addEventListener("change", handleChange);
    firstPercentageInput.addEventListener("blur", handleBlur);

    function handleBlur(e) {
        let value = parseInt(e.target.value);
        if (isNaN(value) || value < 1) value = 100;
        e.target.value = value;
        handleChange(e);
    }
    
    function setVisibilityIbanContainerItems(isVisible){
        const items = document.querySelectorAll('.secondIbanContainer');
        items.forEach(item => {
            setVisibility(item, isVisible);
        });
    }

    function handleChange(e) {
        const raw = e.target.value;

        if (raw === "") {
            setVisibilityIbanContainerItems(false);
            secondPercentageInput.value = "";
            return;
        }

        let value = parseInt(raw);
        if (isNaN(value)) return;

        if (value > 100) {
            value = 100;
            e.target.value = 100;
        }

        if (value === 100) {
            setVisibilityIbanContainerItems( false);
            secondPercentageInput.value = 0;
            return;
        }

        setVisibilityIbanContainerItems( true);
        secondPercentageInput.value = complementOf(value);
    }

    function submitIbanForm (event) {
        if (confirmIbanForm.classList.contains("is-invalid")) return;
        
        event.preventDefault();
       
        const buttons = confirmIbanForm.querySelectorAll("button");
        const inputs = confirmIbanForm.querySelectorAll("input");
        
        const url = confirmIbanForm.getAttribute("action");
        const formData = new URLSearchParams(new FormData(confirmIbanForm));

        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData
        })
            .then(response => response.json())
            .then(responseJson => {
                printErrors("#ibanErrorContainer", responseJson.errors);

                if (responseJson.errors && responseJson.errors.length > 0) {                     
                    buttons.forEach(btn => btn.removeAttribute("disabled"));
                } else {
                    inputs.forEach(input => input.setAttribute("disabled", "disabled"));
                    document.getElementById("waitingForApprovalText")?.removeAttribute('hidden');
                    buttons.forEach(btn => setVisibility(btn, false))
                }
            })
            .catch(() => {
                buttons.forEach(btn => btn.removeAttribute("disabled"));
               
            }).finally(() => {
                removeLoadingState(event.submitter);
        });
    }

    function printErrors(errorContainer, errors) {
        const element = document.querySelector(errorContainer);
        if (!element) return;

        element.innerHTML = "";
        element.classList.add("validation-summary-errors");

        if (errors && errors.length > 0) {
            errors.forEach(error => {
                const span = document.createElement("span");
                span.className = "field-validation-error";
                span.textContent = error;
                element.appendChild(span);
            });
        }
    }
}

